<template>
  <Transition :css="false" @enter="enterTransition" @leave="leaveTransition">
    <slot />
  </Transition>
</template>

<script setup>
const props = defineProps({
  duration: { type: Number, required: false, default: 200 },
  opacity: { type: Boolean, required: false, default: false },
  easingEnter: { type: String, required: false, default: "ease-in-out" },
  easingLeave: { type: String, required: false, default: "ease-in-out" }
});
const getElementStyle = (element) => {
  return {
    height: element.style.height,
    width: element.style.width,
    position: element.style.position,
    visibility: element.style.visibility,
    overflow: element.style.overflow,
    paddingTop: element.style.paddingTop,
    paddingBottom: element.style.paddingBottom,
    borderTopWidth: element.style.borderTopWidth,
    borderBottomWidth: element.style.borderBottomWidth,
    marginTop: element.style.marginTop,
    marginBottom: element.style.marginBottom
  };
};
const prepareElement = (element, initialStyle) => {
  const { width } = getComputedStyle(element);
  element.style.width = width;
  element.style.position = "absolute";
  element.style.visibility = "hidden";
  element.style.height = "";
  const { height } = getComputedStyle(element);
  element.style.width = initialStyle.width;
  element.style.position = initialStyle.position;
  element.style.visibility = initialStyle.visibility;
  element.style.height = "0px";
  element.style.overflow = "hidden";
  return initialStyle.height && initialStyle.height !== "0px" ? initialStyle.height : height;
};
const animateTransition = (element, initialStyle, done, keyframes, options) => {
  const animation = element.animate(keyframes, options);
  element.style.height = initialStyle.height;
  animation.onfinish = () => {
    element.style.overflow = initialStyle.overflow;
    done();
  };
};
const getEnterKeyframes = (height, initialStyle) => [
  {
    height: "0px",
    opacity: props.opacity ? 0 : 1,
    paddingTop: "0px",
    paddingBottom: "0px",
    borderTopWidth: "0px",
    borderBottomWidth: "0px",
    marginTop: "0px",
    marginBottom: "0px"
  },
  {
    height,
    opacity: props.opacity ? 1 : 1,
    paddingTop: initialStyle.paddingTop,
    paddingBottom: initialStyle.paddingBottom,
    borderTopWidth: initialStyle.borderTopWidth,
    borderBottomWidth: initialStyle.borderBottomWidth,
    marginTop: initialStyle.marginTop,
    marginBottom: initialStyle.marginBottom
  }
];
const enterTransition = (element, done) => {
  if (!(element instanceof HTMLElement)) {
    return done();
  }
  const initialStyle = getElementStyle(element);
  const height = prepareElement(element, initialStyle);
  const keyframes = getEnterKeyframes(height, initialStyle);
  const options = { duration: props.duration, easing: props.easingEnter };
  animateTransition(element, initialStyle, done, keyframes, options);
};
const leaveTransition = (element, done) => {
  if (!(element instanceof HTMLElement)) {
    return done();
  }
  const initialStyle = getElementStyle(element);
  const { height } = getComputedStyle(element);
  element.style.height = height;
  element.style.overflow = "hidden";
  const keyframes = getEnterKeyframes(height, initialStyle).reverse();
  const options = { duration: props.duration, easing: props.easingLeave };
  animateTransition(element, initialStyle, done, keyframes, options);
};
</script>
